import postRequest from 'utils/postRequest'
import getRequest from 'utils/getRequest'
import deleteRequest from 'utils/deleteRequest'
import filterUndefinedFromObject from 'utils/filterUndefinedFromObject'
import patchRequest from 'utils/patchRequest'

const path = '/api/criterion-e'
const reassessmentPath = '/api/reassessment/sites'

export const requestCreateCriterionE = (siteId, siteHistoryId) => {
    const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/criterion-e` : `${path}/${siteId}`
    return postRequest(url)
}

export const deleteCriterionE = (criterionEId, siteHistoryId) => {
    const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/criterion-e/${criterionEId}` : `${path}/${criterionEId}`
    return deleteRequest(url)
}

export const requestGetCriterionEs = (siteId, siteHistoryId) => {
    const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/criterion-e` : `${path}/${siteId}`
    return getRequest(url)
}

export const requestGetAllCriterionEs = () => {
    return getRequest(`${path}?select=name`)
}

export const requestUpdateCriterionE = async(criterionEId, siteHistoryId, {
    irreplaceability_score,
    irreplaceability_assessment_approved,
    year,
    trigger_reason_id,
    trigger_reason_text,
}) => {

    const requestBody = filterUndefinedFromObject({
        irreplaceability_score,
        irreplaceability_assessment_approved,
        year,
        trigger_reason_id,
        trigger_reason_text,
    })

    if (siteHistoryId) {
        return await patchRequest(`${reassessmentPath}/${siteHistoryId}/criterion-e/${criterionEId}`, requestBody)
    }
    return await patchRequest(`${path}/${criterionEId}`, requestBody)
}

export const confirmCriterionE = async (criterionEId, isApproved) => {
    return await postRequest(`${path}/${criterionEId}/confirm`, isApproved)
}

export const cancelCriterionEReassessmentRequest = ({ siteId, criterionEId, isCancelled }) => {
    return postRequest(`${reassessmentPath}/${siteId}/criterion-e/${criterionEId}/cancel`, { isCancelled: isCancelled })
}

const buildCriterionEFilesUrl = (siteId, siteHistoryId, suffix = '') => {
  if (!siteId && !siteHistoryId) {
    throw new Error('buildCriterionEFilesUrl: either siteId or siteHistoryId is required')
  }

  if (siteHistoryId) {
    return `${reassessmentPath}/${siteHistoryId}/criterion-e/supporting-docs${suffix}`
  }

  if (!siteId) {
    throw new Error('buildCriterionEFilesUrl: siteId is required when siteHistoryId is not provided')
  }

  return `${path}/${siteId}/supporting-docs${suffix}`
}

export const requestGetCriterionEFiles = async (siteId, siteHistoryId) => {
  const url = buildCriterionEFilesUrl(siteId, siteHistoryId)
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      },
      credentials: 'include' // Include credentials for authentication
    })

    if (response.status === 404) {
      console.warn('[API][criterion-e-files][GET:missing-endpoint]', url)
      return { files: [] }
    }

    let data = null
    try {
      data = await response.json()
    } catch (error) {
      console.warn('[API][criterion-e-files][GET:json-parse-failed]', error)
    }

    if (!response.ok) {
      console.warn('[API][criterion-e-files][GET:failed]', response.status, response.statusText)
      return { files: [] }
    }

    return data ?? { files: [] }
  } catch (error) {
    console.warn('[API][criterion-e-files][GET:error]', error)
    return { files: [] }
  }
}

export const requestUpdateCriterionEFiles = async (siteId, siteHistoryId, formData, options = {}) => {
  const url = buildCriterionEFilesUrl(siteId, siteHistoryId)
  const method = siteHistoryId ? 'PATCH' : 'POST'

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    if (options.onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          options.onProgress(event.loaded, event.total)
        }
      })
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText))
        } catch (error) {
          resolve({})
        }
      } else if (xhr.status === 404) {
        reject(new Error(`Endpoint not found (404). The Criterion E supporting-docs endpoint may not be implemented on the backend yet. URL: ${url}`))
      } else {
        reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Network error'))
    })

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'))
    })

    xhr.open(method, url)
    xhr.setRequestHeader('Accept', 'application/json')
    if (options.file?.name) {
      xhr.setRequestHeader('X-File-Name', encodeURIComponent(options.file.name))
    }
    xhr.withCredentials = true
    xhr.send(formData)
  })
}

export const requestDeleteCriterionEFiles = async (siteId, siteHistoryId, fileId) => {
  if (!fileId) {
    throw new Error('requestDeleteCriterionEFiles: fileId is required')
  }

  const url = buildCriterionEFilesUrl(siteId, siteHistoryId, `/${fileId}`)
  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json'
    },
    credentials: 'include' // Include credentials for authentication
  })

  if (response.status === 204) {
    return {}
  }

  let data = null
  try {
    data = await response.json()
  } catch (error) {
    if (response.ok) {
      return {}
    }
    console.warn('[API][criterion-e-files][DELETE:json-parse-failed]', error)
  }

  if (!response.ok) {
    throw new Error(`Failed to delete criterion E file (${response.status})`)
  }

  return data ?? {}
}
